import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { FaPlay, FaChartLine, FaMap, FaComment } from 'react-icons/fa';
import ResizableLayout from './ResizableLayout';
import PanelToggles from './PanelToggles';
import LayoutDropdown from './LayoutDropdown';
import BikeProfileAnalysisDropdown from './BikeProfileAnalysisDropdown';
import SyncedVideoPlayer from './SyncedVideoPlayer';
import JackSyncedPlotlyCharts from './JackSyncedPlotlyCharts';
import SyncedRouteMap from './SyncedRouteMap';
import RideStatsPanel from './RideStatsPanel';
import SuspensionAnalysisPanel from './SuspensionAnalysisPanel';
import CommentsPanel from './CommentsPanel';
import { runJackPackingAnalysisFromTelemetry } from '../utils/jackPackingEngine';
import {
  getJackParamsForAnalysis,
  buildTrimOverrideForRide,
  getTelemetryTimeBounds,
  loadBikeProfilesStore,
} from '../utils/jackSettings';
import './RideView.css';

const isYouTubeUrl = (url) => typeof url === 'string' && (url.includes('youtube.com') || url.includes('youtu.be'));

/**
 * One run’s main column: same ResizableLayout + panels as RideView (read-only: no import/save header).
 */
export default function CompareRunPane({
  ride,
  trainingZones = null,
  currentTime = 0,
  isPlaying = false,
  onSeek,
  onTimeUpdate,
  onDurationChange,
  /** Called after Firestore update so parent can merge into run list */
  onRideDocumentPatch,
}) {
  const layoutRef = useRef(null);
  const [analysisBikeProfileId, setAnalysisBikeProfileId] = useState(null);
  const [jackPlotXZoom, setJackPlotXZoom] = useState(null);

  useEffect(() => {
    setAnalysisBikeProfileId(null);
    setJackPlotXZoom(null);
  }, [ride?.id]);

  const [showVideo, setShowVideo] = useState(() => {
    try {
      const s = localStorage.getItem('rideViewPanelStates');
      return s ? JSON.parse(s).showVideo ?? true : true;
    } catch {
      return true;
    }
  });
  const [showGraph, setShowGraph] = useState(() => {
    try {
      const s = localStorage.getItem('rideViewPanelStates');
      return s ? JSON.parse(s).showGraph ?? true : true;
    } catch {
      return true;
    }
  });
  const [showMap, setShowMap] = useState(() => {
    try {
      const s = localStorage.getItem('rideViewPanelStates');
      return s ? JSON.parse(s).showMap ?? true : true;
    } catch {
      return true;
    }
  });
  const [showComments, setShowComments] = useState(() => {
    try {
      const s = localStorage.getItem('rideViewPanelStates');
      return s ? JSON.parse(s).showComments ?? false : false;
    } catch {
      return false;
    }
  });
  const [showStats, setShowStats] = useState(() => {
    try {
      const s = localStorage.getItem('rideViewPanelStates');
      if (s) {
        const p = JSON.parse(s);
        if (typeof p.showStats === 'boolean') return p.showStats;
      }
    } catch {
      /* ignore */
    }
    return false;
  });
  const [showSuspension, setShowSuspension] = useState(() => {
    try {
      const s = localStorage.getItem('rideViewPanelStates');
      return s ? JSON.parse(s).showSuspension ?? true : true;
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      let prev = {};
      try {
        prev = JSON.parse(localStorage.getItem('rideViewPanelStates') || '{}');
      } catch {
        prev = {};
      }
      const panelStates = {
        ...prev,
        showVideo,
        showGraph,
        showMap,
        showComments,
        showStats,
        showSuspension,
      };
      localStorage.setItem('rideViewPanelStates', JSON.stringify(panelStates));
    } catch (e) {
      console.error('Failed to save panel states:', e);
    }
  }, [showVideo, showGraph, showMap, showComments, showStats, showSuspension]);

  const hasTelemetry = Array.isArray(ride?.telemetryData) && ride.telemetryData.length > 0;
  const hasGPS = hasTelemetry && ride.telemetryData.some((d) => d.lat && d.lon);

  useEffect(() => {
    let persisted;
    try {
      const s = localStorage.getItem('rideViewPanelStates');
      if (s) {
        const p = JSON.parse(s);
        if (typeof p.showStats === 'boolean') persisted = p.showStats;
      }
    } catch {
      /* ignore */
    }
    if (typeof persisted === 'boolean') return;
    setShowStats(hasGPS);
  }, [ride?.id, hasGPS]);

  const dataOffset = ride?.dataOffset ?? 0;

  const videoUrl =
    ride?.localVideoUrl ||
    ride?.videoUrl ||
    ride?.video_url ||
    (ride?.videos?.length > 0 ? ride.videos[0].url : null);
  const youtubeFromRide = ride?.youtubeUrl || ride?.youtube_url;
  const viewYoutubeUrl = youtubeFromRide || (typeof videoUrl === 'string' && isYouTubeUrl(videoUrl) ? videoUrl : null);
  const viewDirectVideoUrl =
    viewYoutubeUrl || (typeof videoUrl === 'string' && isYouTubeUrl(videoUrl)) ? null : videoUrl;

  const hasPlayableVideo = !!(viewYoutubeUrl || viewDirectVideoUrl);
  const hasVideoOrPending = hasPlayableVideo;

  const duration = useMemo(() => {
    const td = ride?.telemetryData;
    if (!Array.isArray(td) || td.length === 0) {
      if (ride?.duration != null && Number.isFinite(ride.duration)) return ride.duration;
      return 0;
    }
    let minT = Infinity;
    let maxT = -Infinity;
    for (let i = 0; i < td.length; i++) {
      const t = td[i]?.time;
      if (typeof t === 'number' && Number.isFinite(t)) {
        minT = Math.min(minT, t);
        maxT = Math.max(maxT, t);
      }
    }
    if (maxT < 0) return 0;
    return minT > 60 ? Math.max(0, maxT - minT) : maxT;
  }, [ride?.telemetryData, ride?.duration]);

  const jackRideParams = useMemo(() => {
    const td = ride?.telemetryData;
    const base = ride?.jackSuspensionParams ?? ride?.suspensionParams ?? {};
    const trim = buildTrimOverrideForRide(Array.isArray(td) ? td : [], ride?.analysisTrim);
    let out = { ...base, ...trim };
    if (typeof analysisBikeProfileId === 'string' && analysisBikeProfileId) {
      const p = loadBikeProfilesStore().profiles.find((x) => x.id === analysisBikeProfileId);
      if (p) {
        const linkageSource =
          typeof p.linkageSource === 'string' && p.linkageSource.length > 0 ? p.linkageSource : 'custom';
        out = {
          ...out,
          frontTravel: Math.round(Number(p.frontTravel)) || 0,
          rearTravel: Math.round(Number(p.rearTravel)) || 0,
          rearA: Number(p.rearA) || 0,
          rearB: Number(p.rearB) || 0,
          rearC: Number(p.rearC) || 0,
          rearD: Number(p.rearD) || 0,
          linkageSource,
        };
      }
    }
    return out;
  }, [
    ride?.telemetryData,
    ride?.jackSuspensionParams,
    ride?.suspensionParams,
    ride?.analysisTrim,
    analysisBikeProfileId,
  ]);

  const jackAnalysisForRide = useMemo(() => {
    const td = ride?.telemetryData;
    if (!Array.isArray(td) || td.length === 0) return null;
    const params = getJackParamsForAnalysis(jackRideParams);
    return runJackPackingAnalysisFromTelemetry(td, params);
  }, [ride?.telemetryData, jackRideParams]);

  const telemetryTimeBounds = useMemo(
    () => getTelemetryTimeBounds(ride?.telemetryData),
    [ride?.telemetryData],
  );

  const jackChartAnalysisWindow = useMemo(() => {
    const p = getJackParamsForAnalysis(jackRideParams);
    const end =
      p.trimEnd === Infinity || !Number.isFinite(Number(p.trimEnd)) ? telemetryTimeBounds.maxT : Number(p.trimEnd);
    return { start: Number(p.trimStart), end };
  }, [jackRideParams, telemetryTimeBounds.maxT]);

  const handleJackPackingEventSelect = useCallback(
    ({ startTime, endTime }) => {
      const st = Number(startTime);
      const en = Number(endTime);
      if (!Number.isFinite(st) || !Number.isFinite(en)) return;
      const padding = 1.5;
      setJackPlotXZoom({ min: st - padding, max: en + padding });
      if (onSeek) onSeek(st - (Number(dataOffset) || 0));
    },
    [onSeek, dataOffset],
  );

  const handleSaveAiInsight = useCallback(
    async (insightText) => {
      const text = typeof insightText === 'string' ? insightText.trim() : '';
      if (!text || !ride?.id) return;
      const savedAt = Date.now();
      const patch = {
        suspensionAiInsight: text,
        suspensionAiInsightUpdatedAt: savedAt,
      };
      onRideDocumentPatch?.(ride.id, patch);
      try {
        await updateDoc(doc(db, 'runs', ride.id), {
          ...patch,
          updatedAt: serverTimestamp(),
        });
      } catch (err) {
        console.error('Failed to persist AI insight:', err);
      }
    },
    [ride?.id, onRideDocumentPatch],
  );

  const handleLayoutApplied = useCallback(() => {
    window.location.reload();
  }, []);

  const handleResetLayout = useCallback(() => {
    if (layoutRef.current && layoutRef.current.resetLayout) {
      layoutRef.current.resetLayout();
    }
  }, []);

  if (!ride) {
    return <div className="ride-view-container"><p className="compare-summary-muted">No run data.</p></div>;
  }

  return (
    <div className="ride-view-container compare-run-pane">
      <div className="view-controls">
        <PanelToggles
          items={[
            ...(hasVideoOrPending
              ? [{ key: 'video', label: <><FaPlay /> Video</>, checked: showVideo, onChange: setShowVideo }]
              : []),
            ...(hasTelemetry ? [{ key: 'graph', label: <><FaChartLine /> Graph</>, checked: showGraph, onChange: setShowGraph }] : []),
            ...(hasTelemetry ? [{ key: 'stats', label: 'Stats', checked: showStats, onChange: setShowStats }] : []),
            ...(hasTelemetry ? [{ key: 'suspension', label: 'Suspension', checked: showSuspension, onChange: setShowSuspension }] : []),
            ...(hasGPS ? [{ key: 'map', label: <><FaMap /> Map</>, checked: showMap, onChange: setShowMap }] : []),
            { key: 'comments', label: <><FaComment /> Comments</>, checked: showComments, onChange: setShowComments },
          ]}
        />
        <div className="view-controls-right">
          {ride && hasTelemetry ? (
            <BikeProfileAnalysisDropdown value={analysisBikeProfileId} onChange={setAnalysisBikeProfileId} />
          ) : null}
          <LayoutDropdown onLayoutApplied={handleLayoutApplied} onResetLayout={handleResetLayout} />
        </div>
      </div>

      <div className="ride-content-wrapper">
        <ResizableLayout
          ref={layoutRef}
          panelLabels={{
            'ride-info-banner': 'Run info',
            'video-panel': 'Video',
            'telemetry-panel': 'Charts',
            'stats-panel': 'Stats',
            'suspension-panel': 'Suspension analysis',
            'map-panel': 'Map',
            'comments-panel': 'Comments',
          }}
        >
          {hasVideoOrPending && showVideo && (
            <div className="ride-section video-section" data-panel-id="video-panel">
              {hasPlayableVideo ? (
                <SyncedVideoPlayer
                  playerId={`compare-pane-yt-${ride.id}`}
                  videoUrl={viewDirectVideoUrl || null}
                  youtubeUrl={viewYoutubeUrl || undefined}
                  currentTime={currentTime}
                  isPlaying={isPlaying}
                  onTimeUpdate={onTimeUpdate}
                  onDurationChange={onDurationChange}
                  videoOffset={ride?.videoOffset ?? 0}
                />
              ) : (
                <div className="map-placeholder" style={{ padding: 16 }}>
                  <p>No play-in-browser video for this run.</p>
                </div>
              )}
            </div>
          )}

          {hasTelemetry && showGraph && (
            <div className="ride-section graph-section" data-panel-id="telemetry-panel">
              {jackAnalysisForRide?.ok &&
                Array.isArray(jackAnalysisForRide.processedData) &&
                jackAnalysisForRide.processedData.length > 0 && (
                  <JackSyncedPlotlyCharts
                    processedData={jackAnalysisForRide.processedData}
                    analysisResults={jackAnalysisForRide.analysisResults}
                    currentTime={currentTime}
                    dataOffset={dataOffset}
                    onSeek={onSeek}
                    xZoomRange={jackPlotXZoom}
                    onXZoomRangeCleared={() => setJackPlotXZoom(null)}
                    analysisWindow={jackChartAnalysisWindow}
                  />
                )}
            </div>
          )}

          {hasTelemetry && showStats && (
            <div className="ride-section stats-section" data-panel-id="stats-panel">
              <RideStatsPanel
                telemetryData={ride.telemetryData}
                duration={duration}
                trainingZones={trainingZones}
              />
            </div>
          )}

          {hasTelemetry && showSuspension && (
            <div className="ride-section suspension-section" data-panel-id="suspension-panel">
              <SuspensionAnalysisPanel
                telemetryData={ride.telemetryData}
                jackParams={jackRideParams}
                jackAnalysisResult={jackAnalysisForRide}
                onSeek={onSeek}
                dataOffset={dataOffset}
                onJackPackingEventSelect={handleJackPackingEventSelect}
                initialAiInsight={ride?.suspensionAiInsight || ''}
                onSaveAiInsight={handleSaveAiInsight}
              />
            </div>
          )}

          {hasGPS && showMap && (
            <div className="ride-section map-section" data-panel-id="map-panel">
              <SyncedRouteMap
                telemetryData={ride.telemetryData}
                currentTime={currentTime}
                onLocationClick={onSeek}
                dataOffset={dataOffset}
              />
            </div>
          )}

          {!hasGPS && showMap && (
            <div className="ride-section map-section" data-panel-id="map-panel">
              <div className="map-placeholder" style={{ padding: 20 }}>
                <p>No map — no GPS in this run’s telemetry.</p>
              </div>
            </div>
          )}

          {showComments && (
            <div className="ride-section comments-section" data-panel-id="comments-panel">
              <CommentsPanel rideId={ride.id} currentTime={currentTime} onSeek={onSeek} />
            </div>
          )}
        </ResizableLayout>
      </div>
    </div>
  );
}
